/**
 * Pure YouTube ISO-8601 duration helpers.
 *
 * YouTube's contentDetails.duration subset is PT[nH][nM][nS].
 * Machine-readable consumers must use milliseconds; display strings are
 * presentation-only and must never be treated as identity.
 */

const YOUTUBE_DURATION_PATTERN = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/i;

function parseYouTubeDurationParts(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  const match = trimmed.match(YOUTUBE_DURATION_PATTERN);
  if (!match || match.slice(1).every((part) => part === undefined)) {
    return null;
  }

  const hours = match[1] === undefined ? 0 : Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  const seconds = match[3] === undefined ? 0 : Number(match[3]);
  if (![hours, minutes, seconds].every(Number.isSafeInteger)) return null;

  return {
    hours,
    minutes,
    seconds,
    hasHours: match[1] !== undefined,
  };
}

export function parseYouTubeDurationMs(value) {
  const parts = parseYouTubeDurationParts(value);
  if (!parts) return null;

  const durationMs =
    (parts.hours * 3600 + parts.minutes * 60 + parts.seconds) * 1000;
  return Number.isSafeInteger(durationMs) ? durationMs : null;
}

/**
 * Preserves the existing Rhythmé display contract (`03:47`, `1:02:03`).
 * Unknown or malformed input becomes an empty string, never "00:00".
 */
export function formatYouTubeDuration(value) {
  const parts = parseYouTubeDurationParts(value);
  if (!parts) return '';

  const seconds = String(parts.seconds).padStart(2, '0');
  if (parts.hasHours) {
    return `${parts.hours}:${String(parts.minutes).padStart(2, '0')}:${seconds}`;
  }
  return `${String(parts.minutes).padStart(2, '0')}:${seconds}`;
}

export function youtubeDurationFields(isoDuration) {
  return {
    duration: formatYouTubeDuration(isoDuration),
    durationMs: parseYouTubeDurationMs(isoDuration),
  };
}

/**
 * Presentation-only. Unknown/null stays blank; a known 0 ms stays "00:00".
 * Never persist this string.
 */
export function formatDurationMs(durationMs) {
  if (!Number.isSafeInteger(durationMs) || durationMs < 0) return '';

  const totalSeconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
